import { SettingsPermissions } from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { type PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { settingsPagePermission } from "@redotech/redo-merchant-app-common/routing/settings-page-permission";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { ReturnsSettingsTab } from "@redotech/redo-settings-common/flat-team-settings/returns/returns-settings-tab";
import {
  RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
  RETURNS_AUTOMATIONS_SETTINGS_PATH,
  RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
  RETURNS_CONSOLIDATED_SETTINGS_PATH,
  RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
  RETURNS_WAREHOUSE_GRADING_FLOW_SEGMENT,
  RETURNS_WAREHOUSE_VERIFICATION_FLOW_SEGMENT,
} from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { catalogRoutePermission } from "../../../../integrations/catalog/catalog-data";
import { policyEditorBreadcrumb } from "../../../../setting/returns/policies/policy-editor/policy-editor-breadcrumb";
import {
  PRODUCT_EXCHANGE_GUIDANCE_PATH,
  PRODUCTS_AS_VARIANTS_PATH,
  VARIANT_EXCHANGE_GUIDANCE_PATH,
} from "../../../../setting/returns/portal/portal-links";
import { ReturnsSettingsParentBreadcrumb } from "../../../../setting/returns/returns-settings-parent-breadcrumb";
import { ReturnsSettingsTabRedirect } from "../../../../setting/returns/returns-settings-tab-redirect";

export const returnSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("Returns");
      },
      permission: settingsPagePermission("returns-and-claims/returns"),
    },
    path: RETURNS_CONSOLIDATED_SETTINGS_PATH,
    children: [
      {
        path: "",
        lazy: async () => {
          const { ReturnsSettingsPage } =
            await import("../../../../setting/returns/returns-settings-page");
          return { Component: ReturnsSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: policyEditorBreadcrumb,
          permission: settingsPagePermission("returns-and-claims/returns"),
        },
        path: "policies/:policyId",
        lazy: async () => {
          const { ReturnPolicyEditorPage } =
            await import("../../../../setting/returns/policies/policy-editor/return-policy-editor-page");
          return { Component: ReturnPolicyEditorPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Assessments", {
          context: "merchant settings label for return assessments",
        });
      },
      permission: settingsPagePermission("returns-and-claims/assessments"),
    },
    path: "assessments",
    lazy: async () => {
      const { AssessmentsWithTabsPage } =
        await import("../../../../setting/assessments/assessments-with-tabs");
      return { Component: AssessmentsWithTabsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Automations", {
          context: "merchant settings label for return automations",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/operations",
      ),
    },
    path: RETURNS_AUTOMATIONS_SETTINGS_PATH,
    children: [
      {
        index: true,
        element: (
          <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Processing", {
              context: "merchant settings label for return processing flows",
            });
          },
        },
        path: RETURNS_AUTOMATIONS_PROCESSING_SEGMENT,
        children: [
          {
            index: true,
            element: (
              <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />
            ),
          },
          {
            handle: {
              get breadcrumb() {
                return t("Compensation flow");
              },
            },
            path: RETURNS_COMPENSATION_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { CompensationFlowEditor } =
                await import("../../../../setting/flow-editor/compensation-flow/compensation-flow-editor");
              return { Component: CompensationFlowEditor };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Restocking flow");
              },
            },
            path: RETURNS_RESTOCKING_FLOW_EDITOR_SEGMENT,
            lazy: async () => {
              const { RestockingFlowEditor } =
                await import("../../../../setting/flow-editor/restocking-flow/restocking-flow-editor");
              return { Component: RestockingFlowEditor };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Return processing flows");
              },
            },
            path: ":flowId",
            lazy: async () => {
              const { ReturnProcessingFlowBuilder } =
                await import("../../../../setting/return-processing-flows/return-processing-flow-builder");
              return { Component: ReturnProcessingFlowBuilder };
            },
          },
        ],
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Consolidation", {
          context: "merchant settings label for return consolidation",
        });
      },
      permission: settingsPagePermission("returns-and-claims/consolidation"),
    },
    path: "consolidation",
    lazy: async () => {
      const { ConsolidationSettingsPage } =
        // redont-allow-dynamic-import: route-level code splitting
        await import("@redotech/redo-merchant-fulfillment-app/setting/consolidation");
      return { Component: ConsolidationSettingsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Coverage", {
          context: "merchant settings label for return coverage",
        });
      },
      breadcrumbNavigate: false,
      permission: settingsPagePermission("returns-and-claims/returns/coverage"),
    },
    path: "coverage",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="coverage" />,
    children: [
      {
        index: true,
        element: (
          <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.COVERAGE} />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Coverage builder", {
              context:
                "merchant settings breadcrumb for the return coverage builder",
            });
          },
        },
        path: "coverage-builder",
        lazy: async () => {
          const { CoverageBuilderPage } =
            await import("../../../../setting/coverage/coverage-builder/coverage-builder");
          return { Component: CoverageBuilderPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Info modal", {
              context:
                "merchant settings breadcrumb for return coverage information modal",
            });
          },
        },
        path: "info-modal",
        lazy: async () => {
          const { InfoModalPage } =
            await import("../../../../setting/coverage/info-modal");
          return { Component: InfoModalPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Exchanges", {
          context: "merchant settings label for return exchanges",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/operations",
      ),
    },
    path: "exchanges",
    element: <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />,
  },
  {
    handle: {
      get breadcrumb() {
        return t("Integrations", {
          context: "merchant settings label for the integrations catalog",
        });
      },
      permission: settingsPagePermission("general/integrations"),
    },
    path: "integrations",
    children: [
      {
        path: "",
        lazy: async () => {
          const { UnifiedIntegrationsPage } =
            await import("../../../../integrations/pages/unified-integrations-page");
          return { Component: UnifiedIntegrationsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Cordial",
          permission: catalogRoutePermission("cordial"),
        },
        path: "cordial",
        lazy: async () => {
          const { CordialIntegrationSettingsPage } =
            await import("../../../../setting/cordial-integration-settings");
          return { Component: CordialIntegrationSettingsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration");
          },
        },
        path: "vendor/:slug",
        lazy: async () => {
          const { IntegrationVendorPage } =
            await import("../../../../integrations/pages/integration-vendor-page");
          return { Component: IntegrationVendorPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Merchant-created returns", {
          context:
            "merchant settings label for merchant-created return permissions",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/operations",
      ),
    },
    path: "permissions",
    element: <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />,
  },
  {
    handle: {
      get breadcrumb() {
        return t("Point of sale", {
          context: "merchant settings label for point-of-sale returns",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/operations",
      ),
    },
    path: "pos",
    children: [
      {
        index: true,
        element: (
          <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration locations");
          },
        },
        path: "integration-locations",
        lazy: async () => {
          const { IntegrationLocationsPage } =
            await import("../../../../setting/point-of-sale/integration-locations-page");
          return { Component: IntegrationLocationsPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Post processing", {
          context: "merchant settings label for post-return processing",
        });
      },
      permission: settingsPagePermission("returns-and-claims/post-processing"),
    },
    path: "post-processing",
    lazy: async () => {
      const { PostProcessingPage } =
        await import("@redotech/redo-merchant-fulfillment-app/setting/post-processing");
      return { Component: PostProcessingPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Processing groups", {
          context: "merchant settings label for return processing groups",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/processing-groups",
      ),
    },
    path: "processing-groups",
    children: [
      {
        path: "",
        lazy: async () => {
          const { ProcessingGroupsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-groups");
          return { Component: ProcessingGroupsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Group details");
          },
        },
        path: ":groupId",
        lazy: async () => {
          const { ProcessingGroupDetailPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/processing-group-detail");
          return { Component: ProcessingGroupDetailPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Refunds & store credit", {
          context:
            "merchant settings label for return refunds and store credit",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/operations",
      ),
    },
    path: "processing",
    element: <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.OPERATIONS} />,
  },
  {
    handle: {
      get breadcrumb() {
        return t("Products as Variants");
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: PRODUCTS_AS_VARIANTS_PATH,
    lazy: async () => {
      const { ProductsAsVariantsPage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: ProductsAsVariantsPage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Variant {{productName}} guidance", {
          productName: "Exchange AI",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: VARIANT_EXCHANGE_GUIDANCE_PATH,
    lazy: async () => {
      const { VariantExchangeGuidancePage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: VariantExchangeGuidancePage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Product {{productName}} guidance", {
          productName: "Exchange AI",
        });
      },
      permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    },
    path: PRODUCT_EXCHANGE_GUIDANCE_PATH,
    lazy: async () => {
      const { ProductExchangeGuidancePage } =
        await import("../../../../setting/returns/portal/ai-exchange-agent/redo-only-pages");
      return { Component: ProductExchangeGuidancePage };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Return detail view", {
          context: "merchant settings label for the return detail view",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/warehouse",
      ),
    },
    path: "returns-ui",
    lazy: async () => {
      const { ReturnsUiPage } =
        await import("../../../../setting/returns-ui/returns-ui");
      return { element: <ReturnsUiPage /> };
    },
  },
  {
    handle: {
      get breadcrumb() {
        return t("Shipping", {
          context: "merchant settings label for return shipping",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/labels",
        "returns-and-claims/returns/locations",
      ),
    },
    path: "shipping",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="shipping" />,
    children: [
      {
        index: true,
        element: <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.LABELS} />,
      },
      {
        handle: {
          get breadcrumb() {
            return t("Label flow");
          },
        },
        path: "labels-flow-editor",
        lazy: async () => {
          const { LabelsFlowEditor } =
            await import("../../../../setting/flows/labels-flow-editor");
          return { Component: LabelsFlowEditor };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Location flow");
          },
        },
        path: "location-flow-editor",
        lazy: async () => {
          const { LocationFlowEditor } =
            await import("../../../../setting/flows/location-flow-editor");
          return { Component: LocationFlowEditor };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Warehouse", {
          context: "merchant settings label for the return warehouse",
        });
      },
      permission: settingsPagePermission(
        "returns-and-claims/returns/warehouse",
      ),
    },
    path: "warehouse",
    element: <ReturnsSettingsParentBreadcrumb legacyPath="warehouse" />,
    children: [
      {
        index: true,
        element: (
          <ReturnsSettingsTabRedirect tab={ReturnsSettingsTab.WAREHOUSE} />
        ),
      },
      {
        handle: {
          get breadcrumb() {
            return t("Integration");
          },
        },
        path: "integrations/:provider",
        lazy: async () => {
          const { IntegrationDetailPage } =
            await import("../../../../setting/managed-returns/integration-detail");
          return { Component: IntegrationDetailPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Photo guidance");
          },
        },
        path: "photo-guidance",
        lazy: async () => {
          const { PhotoInstructionsPage } =
            // redont-allow-dynamic-import: route-level code splitting
            await import("@redotech/redo-merchant-fulfillment-app/setting/photo-instructions/photo-instructions-page");
          return { Component: PhotoInstructionsPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Processing flow");
          },
        },
        path: "processing-flow",
        children: [
          {
            index: true,
            lazy: async () => {
              const { WarehouseProcessingFlowRedirect } =
                await import("../../../../setting/flows/warehouse-processing-flow-editor");
              return { Component: WarehouseProcessingFlowRedirect };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Verification");
              },
            },
            path: RETURNS_WAREHOUSE_VERIFICATION_FLOW_SEGMENT,
            lazy: async () => {
              const { WarehouseProcessingFlowEditor } =
                await import("../../../../setting/flows/warehouse-processing-flow-editor");
              return {
                element: (
                  <WarehouseProcessingFlowEditor
                    flowType={FlowType.RETURN_PROCESSING_VERIFICATION}
                  />
                ),
              };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Grading");
              },
            },
            path: RETURNS_WAREHOUSE_GRADING_FLOW_SEGMENT,
            lazy: async () => {
              const { WarehouseProcessingFlowEditor } =
                await import("../../../../setting/flows/warehouse-processing-flow-editor");
              return {
                element: (
                  <WarehouseProcessingFlowEditor
                    flowType={FlowType.RETURN_PROCESSING_GRADING}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Product transform");
          },
        },
        path: "product-transform",
        lazy: async () => {
          const { ProductTransformPage } =
            await import("../../../../setting/product/product-transform");
          return { Component: ProductTransformPage };
        },
      },
    ],
  },
  {
    handle: {
      get breadcrumb() {
        return t("Withdrawals", {
          context:
            "merchant settings label for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
        });
      },
      permission: settingsPagePermission("returns-and-claims/withdrawals"),
    },
    path: "withdrawals",
    lazy: async () => {
      const { WithdrawalsPage } =
        await import("@redotech/redo-merchant-general-app/setting/withdrawals/withdrawals-page");
      return { Component: WithdrawalsPage };
    },
  },
];
